import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Briefcase, Clock } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is a service invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A service invoice is a billing document issued by a service provider, such as consultants, designers, developers, agencies, or freelancers, to a client for services rendered. Unlike a product invoice, it lists services, hours, or project milestones rather than physical goods.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I create a service invoice for free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Use Invilic to create a service invoice free online. Choose "Freelancer Invoice", add your services as line items with description, hours/quantity, and rate. The total is calculated automatically. Download a professional PDF instantly, no account needed.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the difference between a service invoice and a product invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A service invoice lists intangible work: hours, consulting sessions, design work, writing, etc. A product invoice lists physical goods with quantities and unit prices. Service invoices often use hourly rates or flat project fees instead of per-unit prices.',
      },
    },
    {
      '@type': 'Question',
      name: 'Should a service invoice include a SAC code in India?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'If you are GST-registered in India and providing services, your GST invoice must include the SAC (Services Accounting Code) for each service. SAC codes are the service equivalent of HSN codes for goods. Invilic\'s GST invoice template has a field for SAC codes.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I bill by the hour on a service invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic lets you add line items with a description, quantity (e.g. hours), and unit rate. The total is calculated automatically. You can add multiple service lines for different types of work on the same invoice.',
      },
    },
  ],
};

export default function ServiceInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Service Invoice Template: Free PDF for Consultants & Freelancers | Invilic"
        description="Free service invoice template for consultants, agencies, and freelancers. Bill by the hour or project. Add SAC codes for GST. Download professional PDF free."
        canonical="https://www.invilic.com/service-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Service Invoice
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Service Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Create professional service invoices online free. Bill by the hour, per project, or per milestone. Perfect for consultants, agencies, IT professionals, and freelancers. Download PDF instantly.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-violet-600/20 transition-all duration-200 text-lg"
          >
            Create Service Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No account needed · PDF in seconds</p>
        </div>

        {/* Use cases */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Who Uses This Service Invoice Template?
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Briefcase, title: 'IT & software consultants', desc: 'Bill clients for development hours, consulting sessions, or fixed-price software projects.' },
              { icon: Clock, title: 'Marketing & creative agencies', desc: 'Invoice for monthly retainers, campaign work, design, copywriting, or ad management.' },
              { icon: Briefcase, title: 'Management consultants', desc: 'Issue professional invoices for advisory services, strategy sessions, or deliverable-based work.' },
              { icon: Clock, title: 'Coaches & trainers', desc: 'Bill for one-on-one sessions, group workshops, online courses, or coaching packages.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-violet-50 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Template fields */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Service Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Your name/business name & logo',
              'Client name and address',
              'Invoice number & issue date',
              'Payment due date',
              'Service description per line item',
              'Hours/quantity and hourly/unit rate',
              'Line item totals calculated automatically',
              'Subtotal, discount & tax',
              'Total amount due',
              'Payment terms & bank details',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-violet-100 dark:border-violet-800/30 bg-violet-50 dark:bg-violet-900/10">
                <CheckCircle className="w-4 h-4 text-violet-600 dark:text-violet-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        {/* GST callout */}
        <section className="mb-16 p-6 rounded-2xl border border-emerald-100 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/20">
          <h2 className="font-bold text-gray-900 dark:text-white mb-2">Need a GST Service Invoice?</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            If you are GST-registered in India, your service invoice must include SAC codes and show CGST/SGST or IGST. Use the dedicated GST invoice template instead.
          </p>
          <Link
            to="/create/gst"
            className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
          >
            Create GST Service Invoice <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Service Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Service Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF in under 2 minutes. No account required.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-violet-600/20 transition-all duration-200 text-lg"
          >
            Use Service Invoice Template <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}

